import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, TextField, Button, Stack, Alert,
  CircularProgress, InputAdornment, IconButton, Chip,
} from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { keyframes } from '@mui/system';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

// ── Theme ──────────────────────────────────────────────────────────────────
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: '#16a34a' },
    secondary: { main: '#B8860B' },
    background: { default: '#f0fdf4', paper: '#ffffff' },
  },
  typography: { fontFamily: '"Syne", "DM Sans", sans-serif' },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { background: '#f0fdf4' },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 16,
            backgroundColor: 'rgba(22,163,74,.04)',
            transition: 'all .25s',
            '& fieldset': { borderColor: 'rgba(22,163,74,.2)', borderWidth: 1.5 },
            '&:hover fieldset': { borderColor: 'rgba(22,163,74,.5)' },
            '&.Mui-focused fieldset': { borderColor: '#16a34a', borderWidth: 2 },
            '&.Mui-focused': { backgroundColor: 'rgba(22,163,74,.06)', boxShadow: '0 0 0 4px rgba(22,163,74,.08)' },
          },
          '& label': { color: 'rgba(22,163,74,.6)', fontWeight: 500, fontFamily: '"DM Sans", sans-serif' },
          '& label.Mui-focused': { color: '#16a34a' },
          '& input': { color: '#052e16', fontFamily: '"DM Sans", sans-serif', letterSpacing: .3 },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: { textTransform: 'none', fontFamily: '"Syne", sans-serif' },
      },
    },
  },
});

// ── Animations ────────────────────────────────────────────────────────────
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(32px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const float = keyframes`
  0%,100% { transform: translateY(0) rotate(-6deg) scale(1); }
  50%      { transform: translateY(-18px) rotate(10deg) scale(1.05); }
`;
const glowPulse = keyframes`
  0%,100% { box-shadow: 0 0 0 0 rgba(22,163,74,.2), 0 8px 32px rgba(22,163,74,.12); }
  50%      { box-shadow: 0 0 0 14px rgba(22,163,74,0), 0 8px 40px rgba(22,163,74,.22); }
`;
const scanline = keyframes`
  0%   { top: -4px; }
  100% { top: 100%; }
`;
const shimmer = keyframes`
  0%   { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;
const borderGlow = keyframes`
  0%,100% { border-color: rgba(22,163,74,.15); }
  50%      { border-color: rgba(22,163,74,.4); }
`;

// ── API ───────────────────────────────────────────────────────────────────
const API = process.env.REACT_APP_API_URL;

const parseResponse = async (res, url) => {
  const text = await res.text();
  try { return JSON.parse(text); }
  catch {
    throw new Error(
      !API
        ? 'REACT_APP_API_URL non défini dans .env'
        : `Erreur serveur (statut ${res.status}) sur "${url}"`
    );
  }
};

const callLogin = async (phone, password) => {
  const url = `${API}/auth/login`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ phone, password }),
  });
  const json = await parseResponse(res, url);
  if (!res.ok || !json.success) throw new Error(json.message || 'Identifiants incorrects');
  return json;
};

const saveSession = (json) => {
  localStorage.setItem('accessToken', json.accessToken);
  localStorage.setItem('refreshToken', json.refreshToken);
  localStorage.setItem('user', JSON.stringify(json.data));
};

// ── Grid dots background ──────────────────────────────────────────────────
const DotGrid = () => (
  <Box sx={{
    position: 'absolute', inset: 0, pointerEvents: 'none',
    backgroundImage: 'radial-gradient(circle, rgba(22,163,74,.1) 1px, transparent 1px)',
    backgroundSize: '28px 28px',
    maskImage: 'radial-gradient(ellipse 80% 80% at 50% 50%, black 40%, transparent 100%)',
  }} />
);

// ── Main Component ────────────────────────────────────────────────────────
const AuthPage = () => {
  const navigate = useNavigate();
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [phone, setPhone]     = useState('');
  const [password, setPassword] = useState('');
  const [focused, setFocused] = useState(null);

  const handleSubmit = async () => {
    setError(''); setLoading(true);
    try {
      const json = await callLogin(phone, password);
      saveSession(json);
      navigate(`/profile/${json.data._id}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'radial-gradient(ellipse 120% 80% at 50% -10%, rgba(22,163,74,.08) 0%, #f0fdf4 60%)',
        position: 'relative', overflow: 'hidden', px: 2,
      }}>

        {/* Dot grid */}
        <DotGrid />

        {/* Large glow orb top */}
        <Box sx={{
          position: 'absolute', top: '-20%', left: '50%', transform: 'translateX(-50%)',
          width: 700, height: 400, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(22,163,74,.06) 0%, transparent 70%)',
          pointerEvents: 'none',
          filter: 'blur(40px)',
        }} />

        {/* Bottom right orb */}
        <Box sx={{
          position: 'absolute', bottom: '-10%', right: '-10%',
          width: 500, height: 400, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(184,134,11,.05) 0%, transparent 70%)',
          pointerEvents: 'none', filter: 'blur(50px)',
        }} />

        {/* Floating soccer balls */}
        <Box sx={{
          position: 'absolute', top: '8%', right: '8%',
          animation: `${float} 6s ease-in-out infinite`,
          opacity: .18, filter: 'drop-shadow(0 0 12px rgba(22,163,74,.3))',
        }}>
          <SportsSoccerIcon sx={{ fontSize: 80, color: '#16a34a' }} />
        </Box>
        <Box sx={{
          position: 'absolute', bottom: '10%', left: '6%',
          animation: `${float} 8s ease-in-out infinite`, animationDelay: '2s',
          opacity: .12, filter: 'drop-shadow(0 0 8px rgba(184,134,11,.3))',
        }}>
          <SportsSoccerIcon sx={{ fontSize: 52, color: '#B8860B' }} />
        </Box>

        {/* Card */}
        <Paper elevation={0} sx={{
          position: 'relative', zIndex: 1, width: '100%', maxWidth: 440,
          borderRadius: '28px',
          border: '1.5px solid rgba(22,163,74,.15)',
          background: 'linear-gradient(160deg, #ffffff 0%, #f9fefb 100%)',
          boxShadow: '0 20px 60px rgba(22,163,74,.08), 0 4px 16px rgba(0,0,0,.06), 0 0 0 1px rgba(22,163,74,.06), inset 0 1px 0 rgba(255,255,255,.9)',
          overflow: 'hidden',
          animation: `${fadeUp} .7s cubic-bezier(.22,.68,0,1.15) forwards, ${borderGlow} 4s ease-in-out infinite`,
        }}>

          {/* Scanline effect */}
          <Box sx={{
            position: 'absolute', left: 0, right: 0, height: '2px', zIndex: 10, pointerEvents: 'none',
            background: 'linear-gradient(90deg, transparent, rgba(22,163,74,.1), transparent)',
            animation: `${scanline} 4s linear infinite`,
          }} />

          {/* Top shimmer strip */}
          <Box sx={{
            height: 3,
            background: 'linear-gradient(90deg, transparent, #16a34a, #B8860B, #16a34a, transparent)',
            backgroundSize: '400px 100%',
            animation: `${shimmer} 3s linear infinite`,
          }} />

          <Box sx={{ p: { xs: 3.5, sm: 5 }, pt: 4.5 }}>

            {/* Logo area */}
            <Stack alignItems="center" mb={4.5}>
              <Box sx={{
                width: 68, height: 68, borderRadius: '22px',
                background: 'linear-gradient(145deg, #dcfce7, #f0fdf4)',
                border: '1.5px solid rgba(22,163,74,.25)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2.5,
                animation: `${glowPulse} 3s ease-in-out infinite`,
                position: 'relative',
                boxShadow: '0 4px 16px rgba(22,163,74,.1)',
                '&::after': {
                  content: '""', position: 'absolute', inset: -1, borderRadius: '22px',
                  background: 'radial-gradient(circle at 30% 20%, rgba(22,163,74,.08), transparent 70%)',
                },
              }}>
                <SportsSoccerIcon sx={{ color: '#16a34a', fontSize: 34, filter: 'drop-shadow(0 0 6px rgba(22,163,74,.4))' }} />
              </Box>

              <Typography sx={{
                fontSize: '2rem', fontWeight: 900, color: '#052e16',
                fontFamily: '"Syne", sans-serif', letterSpacing: -1,
                textShadow: '0 0 30px rgba(22,163,74,.1)',
                lineHeight: 1,
              }}>
                ProPlayeur
              </Typography>
              <Typography sx={{
                color: 'rgba(22,163,74,.55)', fontSize: '.75rem', mt: .8, fontWeight: 500,
                letterSpacing: 2, textTransform: 'uppercase', fontFamily: '"DM Sans", sans-serif',
              }}>
                Gestion des talents sportifs
              </Typography>
            </Stack>

            {/* Heading */}
            <Box mb={3.5}>
              <Typography sx={{
                fontWeight: 800, fontSize: '1.25rem', color: '#052e16',
                fontFamily: '"Syne", sans-serif', mb: .5,
              }}>
                Bon retour 👋
              </Typography>
              <Typography sx={{ color: 'rgba(5,46,22,.45)', fontSize: '.88rem', fontWeight: 400 }}>
                Connectez-vous pour accéder à votre espace.
              </Typography>
            </Box>

            {/* Error */}
            {error && (
              <Alert severity="error" sx={{
                mb: 2.5, borderRadius: 3,
                bgcolor: 'rgba(239,68,68,.06)', border: '1px solid rgba(239,68,68,.2)',
                color: '#b91c1c', '& .MuiAlert-icon': { color: '#ef4444' }, fontSize: '.85rem',
              }}>
                {error}
              </Alert>
            )}

            {/* Fields */}
            <Stack spacing={2} onKeyDown={e => e.key === 'Enter' && handleSubmit()}>

              {/* Phone */}
              <Box sx={{ position: 'relative' }}>
                <TextField
                  fullWidth
                  label="Numéro de téléphone"
                  type="tel"
                  value={phone}
                  onChange={e => { setPhone(e.target.value); setError(''); }}
                  onFocus={() => setFocused('phone')}
                  onBlur={() => setFocused(null)}
                  placeholder="+224 6XX XXX XXX"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneAndroidIcon sx={{
                          color: focused === 'phone' ? '#16a34a' : 'rgba(22,163,74,.45)',
                          fontSize: 20, transition: 'color .2s',
                        }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {/* Password */}
              <TextField
                fullWidth
                label="Mot de passe"
                type={showPwd ? 'text' : 'password'}
                value={password}
                onChange={e => { setPassword(e.target.value); setError(''); }}
                onFocus={() => setFocused('pwd')}
                onBlur={() => setFocused(null)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlinedIcon sx={{
                        color: focused === 'pwd' ? '#16a34a' : 'rgba(22,163,74,.45)',
                        fontSize: 20, transition: 'color .2s',
                      }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPwd(v => !v)} edge="end" size="small"
                        sx={{ color: 'rgba(22,163,74,.45)', '&:hover': { color: '#16a34a' }, transition: 'color .2s' }}>
                        {showPwd
                          ? <VisibilityOffOutlinedIcon fontSize="small" />
                          : <VisibilityOutlinedIcon fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>

            {/* Forgot */}
            <Box sx={{ textAlign: 'right', mt: 1.5 }}>
              <Typography variant="caption" sx={{
                color: 'rgba(22,163,74,.55)', fontWeight: 600, cursor: 'pointer',
                '&:hover': { color: '#16a34a', textDecoration: 'underline' },
                transition: 'color .2s',
              }}>
                Mot de passe oublié ?
              </Typography>
            </Box>

            {/* Submit */}
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleSubmit}
              disabled={loading}
              endIcon={loading
                ? <CircularProgress size={18} sx={{ color: '#ffffff' }} />
                : <ArrowForwardIcon />}
              sx={{
                mt: 3.5, py: 1.8, borderRadius: '14px',
                fontWeight: 800, fontSize: '1rem',
                fontFamily: '"Syne", sans-serif', letterSpacing: .3,
                color: '#ffffff',
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 60%, #15803d 100%)',
                boxShadow: '0 6px 24px rgba(22,163,74,.3), 0 2px 8px rgba(0,0,0,.1)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #4ade80 0%, #22c55e 60%, #16a34a 100%)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 12px 32px rgba(22,163,74,.35), 0 4px 12px rgba(0,0,0,.12)',
                },
                '&:active': { transform: 'translateY(0)', boxShadow: '0 4px 16px rgba(22,163,74,.2)' },
                '&:disabled': {
                  background: 'rgba(22,163,74,.12)', color: 'rgba(22,163,74,.4)', boxShadow: 'none',
                },
                transition: 'all .25s cubic-bezier(.22,.68,0,1.2)',
              }}>
              {loading ? 'Connexion en cours...' : 'Se connecter'}
            </Button>

            {/* Footer chip */}
            <Stack alignItems="center" mt={3}>
              <Chip
                label="🔒 Connexion sécurisée · JWT · ProPlayeur 2025"
                size="small"
                sx={{
                  bgcolor: 'rgba(22,163,74,.06)',
                  color: 'rgba(22,163,74,.5)',
                  fontSize: '.68rem',
                  border: '1px solid rgba(22,163,74,.12)',
                  fontWeight: 500,
                  fontFamily: '"DM Sans", sans-serif',
                }}
              />
            </Stack>
          </Box>
        </Paper>
      </Box>
    </ThemeProvider>
  );
};

export default AuthPage;